import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useIdentity } from '../features/identity/state/useIdentity';
import { subscriptionGateway } from '../features/subscription';
import { PLAN_TIER } from '../features/subscription/types';
import './ProfileMenu.css';

interface MenuItem {
  label: string;
  to: string;
  icon: string;
}

/** EPN-46 — exact order and exact 6 items from the approved "[FINAL] User Account Dropdown" reference
 * (see User Account Dropdown.pdf / the [Admin] User Account Dropdown Figma export): My Profile,
 * Subscription, Support, Change Password, Security Settings, then Sign out (rendered separately, below).
 * Company Profile, Payroll Settings, FUTA Credit Reduction, and Help & Support were removed here — none
 * appear in any approved screenshot. Company Profile and Payroll Settings are still reachable from
 * EPN-45's sidebar (Settings > Company > Company Details, Settings > Payroll > Tax Payment Settings); FUTA
 * Credit Reduction and Help & Support have no other destination today — flagged in this PR, not silently
 * dropped. "Security Settings" reuses the existing /security-workspace route/page (label corrected to
 * match the approved design; the page itself is unchanged). Destinations route to a real screen where one
 * exists, or a clearly-labeled placeholder otherwise — never a dead link. */
const MENU_ITEMS: MenuItem[] = [
  { label: 'My Profile', to: '/profile', icon: 'fa-user-circle-o' },
  { label: 'Subscription', to: '/settings/subscription', icon: 'fa-credit-card' },
  { label: 'Support', to: '/support', icon: 'fa-headphones' },
  { label: 'Change Password', to: '/change-password', icon: 'fa-lock' },
  { label: 'Security Settings', to: '/security-workspace', icon: 'fa-wrench' },
];

function subscriptionBadgeLabel(planTier: number): string | null {
  switch (planTier) {
    case PLAN_TIER.Core: return 'CORE';
    case PLAN_TIER.Plus: return 'PLUS';
    case PLAN_TIER.ProPlus: return 'PRO PLUS';
    default: return null;
  }
}

function initialsOf(text: string): string {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[words.length - 1]![0]!).toUpperCase();
}

/**
 * The global "My Profile" trigger + dropdown, shared between AppShell's topbar and the standalone
 * Dashboard's own header — see docs/profile-menu-architecture.md for why one component covers both.
 * Every displayed value (name, company, role, subscription badge) comes from the authenticated session
 * (`useIdentity`) or a real subscription lookup — nothing here is hard-coded.
 *
 * Account/profile actions only — company selection/switching is `shared/BusinessViewSwitcher.tsx`'s
 * responsibility (EPN-47 moved the former "Switch Account" membership-card list there; see
 * docs/profile-menu-architecture.md and docs/business-view-switcher.md for the split).
 */
export function ProfileMenu() {
  const { me, logout } = useIdentity();
  const [open, setOpen] = useState(false);
  const [badgeLabel, setBadgeLabel] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const active = me?.memberships.find((m) => m.companyId === me.activeCompanyId) ?? null;

  const activeCompanyId = me?.activeCompanyId;

  const loadBadge = useCallback(async (signal?: AbortSignal) => {
    if (!activeCompanyId) {
      setBadgeLabel(null);
      return;
    }
    try {
      const subscription = await subscriptionGateway.getCurrentSubscription(signal);
      // A plan badge is shown only while the plan's features are actually in force — a trial that has not
      // started or has expired must not still read as "PRO PLUS" (no contradictory plan labels).
      setBadgeLabel(subscription?.grantsPlanEntitlement ? subscriptionBadgeLabel(subscription.planTier) : null);
    } catch {
      setBadgeLabel(null); // Informational badge only — a lookup failure just hides it.
    }
  }, [activeCompanyId]);

  useEffect(() => {
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadBadge(controller.signal);
    return () => controller.abort();
  }, [loadBadge]);

  // Route changes and tenant switches always close the menu — a stale open dropdown pointing at the
  // previous screen's context would be confusing, and the assignment calls this out explicitly.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOpen(false);
  }, [location.pathname, activeCompanyId]);

  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const items = menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]');
      if (!items || items.length === 0) return;
      event.preventDefault();
      const currentIndex = Array.prototype.indexOf.call(items, document.activeElement);
      const nextIndex = event.key === 'ArrowDown'
        ? (currentIndex + 1) % items.length
        : (currentIndex - 1 + items.length) % items.length;
      items[nextIndex]?.focus();
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      const first = menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]');
      first?.focus();
    }
  }, [open]);

  if (!me) {
    return null;
  }

  const displayName = me.displayName ?? me.email;

  async function handleSignOut() {
    setOpen(false);
    await logout();
    navigate('/login');
  }

  return (
    <div className="profile-menu" ref={containerRef}>
      <button
        type="button"
        ref={triggerRef}
        className="profile-menu-trigger"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`Account menu for ${displayName}`}
      >
        <span className="profile-menu-avatar" aria-hidden="true">{initialsOf(displayName)}</span>
        <span className="profile-menu-trigger-text">
          <span className="profile-menu-name">{displayName}</span>
          <span className="profile-menu-meta">{active?.companyName ?? 'No company'} &ndash; {active?.roleName ?? 'No role'}</span>
        </span>
        <i className={`fa fa-chevron-down profile-menu-chevron${open ? ' profile-menu-chevron--open' : ''}`} aria-hidden="true" />
      </button>

      {open && (
        <div className="profile-menu-dropdown" role="menu" aria-label="Account" ref={menuRef}>
          {MENU_ITEMS.map((item) => (
            <Link key={item.to} to={item.to} role="menuitem" className="profile-menu-item" tabIndex={-1}>
              <i className={`fa ${item.icon} profile-menu-item-icon`} aria-hidden="true" />
              <span>{item.label}</span>
              {item.label === 'Subscription' && badgeLabel && (
                <span className="profile-menu-badge">{badgeLabel}</span>
              )}
            </Link>
          ))}

          <button type="button" role="menuitem" tabIndex={-1} className="profile-menu-item profile-menu-item--danger" onClick={() => void handleSignOut()}>
            <i className="fa fa-sign-out profile-menu-item-icon" aria-hidden="true" />
            <span>Sign out</span>
          </button>
        </div>
      )}
    </div>
  );
}
